import { NavLink } from 'react-router-dom'
import {
  LayoutDashboard,
  CreditCard,
  BarChart3,
  DollarSign,
  FileText,
  Globe,
  History,
  Link2,
  Menu,
  MousePointerClick,
  Newspaper,
  ShieldAlert,
  Siren,
  Tag,
  Users,
  X,
} from 'lucide-react'
import { useState } from 'react'
import { useAuth } from '../contexts/AuthContext'
import { cn } from './ui'

type NavItemDef = {
  name: string
  href: string
  icon: typeof LayoutDashboard
  superAdminOnly?: boolean
}

const navigation: NavItemDef[] = [
  { name: 'Dashboard',          href: '/',                   icon: LayoutDashboard },
  { name: 'Users',              href: '/users',              icon: Users },
  { name: 'Credit Cards',       href: '/cards',              icon: CreditCard },
  { name: 'MITC Management',    href: '/mitc',               icon: FileText },
  { name: 'Offers Review',      href: '/offers',             icon: Tag },
  { name: 'Offer Sources',      href: '/offer-sources',      icon: Globe },
  { name: 'News Review',        href: '/news',               icon: Newspaper },
  { name: 'Referral Links',     href: '/referrals',          icon: Link2 },
  { name: 'Referral Analytics', href: '/referral-analytics', icon: MousePointerClick },
  { name: 'Analytics',          href: '/analytics',          icon: BarChart3 },
  { name: 'Cost Dashboard',     href: '/cost-dashboard',     icon: DollarSign },
  { name: 'Grievances',         href: '/grievances',         icon: ShieldAlert },
  { name: 'Breach Notice',      href: '/breach-notice',      icon: Siren, superAdminOnly: true },
  { name: 'Audit Log',          href: '/audit-log',          icon: History },
]

function NavItem({
  href,
  icon: Icon,
  name,
  onSelect,
}: {
  href: string
  icon: typeof LayoutDashboard
  name: string
  onSelect?: () => void
}) {
  return (
    <NavLink
      to={href}
      end={href === '/'}
      onClick={onSelect}
      className={({ isActive }) =>
        cn(
          'group relative flex items-center gap-3 rounded-xl px-3.5 py-2.5 text-sm font-medium',
          'transition-colors duration-150',
          isActive
            ? 'bg-surface-elevated text-ink'
            : 'text-ink-muted hover:text-ink hover:bg-surface-elevated/60',
        )
      }
    >
      {({ isActive }) => (
        <>
          {/* Gold accent bar for active state */}
          <span
            aria-hidden
            className={cn(
              'absolute left-0 top-1/2 -translate-y-1/2 h-6 w-1 rounded-full bg-accent transition-opacity duration-150',
              isActive ? 'opacity-100' : 'opacity-0',
            )}
          />
          <Icon size={18} className={cn(isActive ? 'text-brand-bright' : 'text-ink-subtle group-hover:text-ink')} />
          <span>{name}</span>
        </>
      )}
    </NavLink>
  )
}

function SidebarContents({ onSelect }: { onSelect?: () => void }) {
  const { user } = useAuth()

  return (
    <div className="flex h-full flex-col">
      {/* Brand */}
      <div className="flex h-16 items-center px-5 border-b border-line">
        <div className="h-9 w-9 rounded-xl bg-gradient-to-br from-brand-bright to-brand-deep shadow-glow-teal flex items-center justify-center">
          <span className="font-display text-sm text-white">SS</span>
        </div>
        <span className="ml-3 font-display text-lg text-ink">SwipeSage</span>
      </div>

      {/* Nav */}
      <nav className="flex-1 px-3 py-5 space-y-1">
        {navigation
          .filter(item => !item.superAdminOnly || user?.role === 'super_admin')
          .map(item => (
            <NavItem key={item.name} {...item} onSelect={onSelect} />
          ))}
      </nav>

      {/* User badge */}
      <div className="border-t border-line p-4">
        <div className="flex items-center gap-3">
          <div className="h-9 w-9 rounded-full bg-gradient-to-br from-brand to-brand-deep flex items-center justify-center">
            <span className="text-white text-sm font-semibold">
              {user?.email?.charAt(0).toUpperCase() ?? 'A'}
            </span>
          </div>
          <div className="min-w-0">
            <p className="truncate text-sm font-medium text-ink">{user?.email}</p>
            <p className="text-xs text-ink-subtle">Admin</p>
          </div>
        </div>
      </div>
    </div>
  )
}

export default function Sidebar() {
  const [sidebarOpen, setSidebarOpen] = useState(false)

  return (
    <>
      {/* Mobile overlay sidebar */}
      <div className={`fixed inset-0 z-50 lg:hidden ${sidebarOpen ? 'block' : 'hidden'}`}>
        <div
          className="fixed inset-0 bg-black/60 backdrop-blur-sm"
          onClick={() => setSidebarOpen(false)}
        />
        <div className="fixed inset-y-0 left-0 flex w-64 flex-col bg-surface border-r border-line shadow-lg">
          <div className="absolute top-4 right-4">
            <button
              onClick={() => setSidebarOpen(false)}
              className="p-1.5 rounded-lg text-ink-subtle hover:text-ink hover:bg-surface-elevated"
              aria-label="Close menu"
            >
              <X size={20} />
            </button>
          </div>
          <SidebarContents onSelect={() => setSidebarOpen(false)} />
        </div>
      </div>

      {/* Desktop sidebar */}
      <div className="hidden lg:flex lg:flex-shrink-0">
        <div className="flex w-64 flex-col bg-surface border-r border-line">
          <SidebarContents />
        </div>
      </div>

      {/* Mobile menu button */}
      <div className="lg:hidden fixed top-4 left-4 z-40">
        <button
          onClick={() => setSidebarOpen(true)}
          className="p-2 rounded-xl text-ink-muted hover:text-ink bg-surface border border-line shadow-sm"
          aria-label="Open menu"
        >
          <Menu size={20} />
        </button>
      </div>
    </>
  )
}
